/**
 * Myoform Guided Quiz — presentational components.
 *
 * Every function here is pure: config (+ current selection) in, HTML string
 * out. None of them read or write state, so the flow logic in
 * myoform-quiz.js can be changed without touching markup, and markup can be
 * changed without touching logic.
 *
 * Exposed as window.MyoQuizComponents:
 *   QuizLayout, QuizProgress, OptionsNote, OptionCard, ProductCard, GoalCard,
 *   TestimonialCard, PricingCard, OrderSummary, UpsellCard,
 *   StickyContinueButton
 *
 * All content originates from snippets/quiz-config.liquid.
 */
(function () {
  'use strict';

  /* ======================================================================
     Helpers
     ====================================================================== */

  /** Escapes text destined for an HTML text node or attribute value. */
  function esc(value) {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Drops falsy entries so conditional markup can be composed inline. */
  function join(parts) {
    return parts.filter(Boolean).join('');
  }

  var ICONS = {
    check:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>',
    checkThin:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>',
    arrowLeft:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>',
    arrowRight:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>',
    refresh:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-2.2-5.9"/><polyline points="21 3 21 9 15 9"/></svg>',
    star:
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>',
    bolt:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>',
    leaf:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></svg>',
    muscle:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6.5 6.5h11"/><path d="M6.5 17.5h11"/><path d="M4 12h16"/><path d="M4 9.5v5"/><path d="M20 9.5v5"/></svg>',
    moon:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>',
    heart:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l8.84 8.84 8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/></svg>',
    spark:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8"/></svg>',
    dna:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 3c0 6 10 12 10 18"/><path d="M17 3c0 6-10 12-10 18"/><path d="M8.5 7.5h7"/><path d="M8.5 16.5h7"/></svg>',
    shield:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
    info:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="16.5"/><circle cx="12" cy="7.8" r="0.9" fill="currentColor" stroke="none"/></svg>'
  };

  function icon(name) {
    return ICONS[name] || '';
  }

  /* ======================================================================
     Small shared partials
     ====================================================================== */

  function badge(text, modifier) {
    if (!text) return '';
    var cls = 'mqz-badge' + (modifier ? ' mqz-badge--' + modifier : '');
    return '<span class="' + cls + '">' + esc(text) + '</span>';
  }

  /** The selected-state checkmark shared by every selectable card. */
  function checkMark() {
    return '<span class="mqz-card__check" aria-hidden="true">' + icon('check') + '</span>';
  }

  function benefitList(items, className) {
    if (!items || !items.length) return '';
    return (
      '<ul class="' +
      className +
      '">' +
      items
        .map(function (item) {
          return '<li>' + icon('checkThin') + '<span>' + esc(item) + '</span></li>';
        })
        .join('') +
      '</ul>'
    );
  }

  function priceBlock(price, compareAt, note, formatMoney) {
    return join([
      '<span class="mqz-price">' + esc(formatMoney(price)) + '</span>',
      compareAt ? '<span class="mqz-price mqz-price--was">' + esc(formatMoney(compareAt)) + '</span>' : '',
      note ? '<span class="mqz-price__note">' + esc(note) + '</span>' : ''
    ]);
  }

  /**
   * Shared attributes for a selectable card. `role` is radio for
   * single-select steps and checkbox for multi-select, which gives keyboard
   * users the right semantics without a real form control.
   */
  function cardAttrs(opts) {
    return join([
      ' type="button"',
      ' role="' + opts.role + '"',
      ' aria-checked="' + (opts.selected ? 'true' : 'false') + '"',
      ' data-mqz-option="' + esc(opts.id) + '"',
      ' tabindex="' + (opts.tabbable === false ? '-1' : '0') + '"'
    ]);
  }

  /* ======================================================================
     QuizProgress
     ====================================================================== */

  /**
   * Quiet progress affordance: a back button, a hairline bar and an
   * "N of M" counter. `total` is recomputed by the caller whenever the
   * projected path changes (skipping the plan step shortens the flow).
   */
  function QuizProgress(state) {
    var pct = state.total > 0 ? Math.round((state.current / state.total) * 100) : 0;
    var label = String(state.template || '{current} of {total}')
      .replace('{current}', state.current)
      .replace('{total}', state.total);

    return join([
      '<div class="mqz__progress">',
      '<button type="button" class="mqz__progress-back" data-mqz-back',
      state.canGoBack ? '' : ' hidden',
      '>',
      icon('arrowLeft'),
      '<span>' + esc(state.backLabel || 'Back') + '</span>',
      '</button>',

      '<div class="mqz__progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="100"',
      ' aria-valuenow="' + pct + '" aria-label="' + esc(label) + '">',
      '<span class="mqz__progress-fill" style="width:' + pct + '%"></span>',
      '</div>',

      '<span class="mqz__progress-count">' + esc(label) + '</span>',
      '</div>'
    ]);
  }

  /* ======================================================================
     QuizLayout
     ====================================================================== */

  /**
   * Wraps a step: eyebrow, headline, subheading, then the step body and the
   * footer actions. Every step goes through this so spacing and rhythm stay
   * identical across the flow.
   *
   * The step's `layout` is echoed as a modifier class, so a new step that picks
   * an existing layout inherits its whole treatment from CSS with no new rules.
   */
  function QuizLayout(step, body, footer) {
    return join([
      '<section class="mqz__step mqz__step--enter',
      step.layout ? ' mqz__step--' + esc(step.layout) : '',
      '" data-mqz-step="' + esc(step.id) + '"',
      ' aria-labelledby="mqz-headline-' + esc(step.id) + '">',

      '<header class="mqz__head">',
      step.eyebrow ? '<p class="mqz__eyebrow">' + esc(step.eyebrow) + '</p>' : '',
      '<h1 class="mqz__headline" id="mqz-headline-' + esc(step.id) + '">' + esc(step.headline) + '</h1>',

      step.tooltip
        ? join([
            '<p class="mqz__tip">',
            '<span class="mqz__tip-icon" aria-hidden="true">' + icon('info') + '</span>',
            '<span class="mqz__tip-text">' + esc(step.tooltip) + '</span>',
            '</p>'
          ])
        : '',

      step.subheading ? '<p class="mqz__subheading">' + esc(step.subheading) + '</p>' : '',
      '</header>',

      body,
      footer || '',
      '</section>'
    ]);
  }

  /**
   * Footnote under a step's options. Quieter than the tip above them: no
   * panel, just an "i" and a line of small text qualifying what the cards say.
   */
  function OptionsNote(text, ctx) {
    if (!text) return '';

    return join([
      '<p class="mqz__note">',
      '<span class="mqz__note-icon" aria-hidden="true">' + icon('info') + '</span>',
      '<span class="mqz__note-text">' + esc(ctx.interpolate(text)) + '</span>',
      '</p>'
    ]);
  }

  /* ======================================================================
     Interstitial  — a beat between questions: statement, image, social proof
     ====================================================================== */

  function Interstitial(step) {
    var media = step.media || {};
    var proof = step.proof || {};
    var quote = step.quote || {};

    return join([
      '<div class="mqz-inter">',

      media.image
        ? join([
            '<div class="mqz-inter__media">',
            '<img src="' + esc(media.image) + '" alt="' + esc(media.imageAlt || '') + '" decoding="async">',
            '</div>'
          ])
        : '',

      proof.image
        ? join([
            '<div class="mqz-inter__proof">',
            '<img src="' + esc(proof.image) + '" alt="' + esc(proof.imageAlt || '') + '" loading="lazy" decoding="async">',
            '</div>'
          ])
        : '',

      /* Athlete endorsement. The text alone is enough to render it; the
         portrait and the credential line are both optional. */
      quote.text
        ? join([
            '<figure class="mqz-inter__quote">',
            quote.image
              ? join([
                  '<div class="mqz-inter__quote-portrait">',
                  '<img src="' +
                    esc(quote.image) +
                    '" alt="' +
                    esc(quote.imageAlt || quote.name || '') +
                    '" loading="lazy" decoding="async">',
                  '</div>'
                ])
              : '',
            '<div class="mqz-inter__quote-body">',
            '<blockquote class="mqz-inter__quote-text">&ldquo;' + esc(quote.text) + '&rdquo;</blockquote>',
            quote.name
              ? join([
                  '<figcaption class="mqz-inter__quote-name">',
                  esc(quote.name),
                  quote.detail ? '<span>' + esc(quote.detail) + '</span>' : '',
                  '</figcaption>'
                ])
              : '',
            '</div>',
            '</figure>'
          ])
        : '',

      '</div>'
    ]);
  }

  /* ======================================================================
     ProductCard  — Step 1 (DNA)
     ====================================================================== */

  /**
   * Premium product card: large image, optional badge, title, one short line
   * of copy, benefits, optional social proof, price on a divided footer.
   */
  function ProductCard(option, ctx) {
    /* Priced steps (DNA) show a divided price footer; unpriced ones
       (supplements) end at the copy, with no empty rule left behind. */
    var hasPrice = option.price !== null && option.price !== undefined;
    var stars = '';
    if (option.rating) {
      var starCount = option.rating.stars || 5;
      var starRow = '';
      for (var i = 0; i < starCount; i++) starRow += icon('star');

      stars =
        '<div class="mqz-product__rating">' +
        '<span class="mqz-product__stars" aria-hidden="true">' +
        starRow +
        '</span>' +
        '<span>' +
        esc(option.rating.score) +
        (option.rating.count ? ' · ' + esc(option.rating.count) : '') +
        '</span>' +
        '</div>';
    }

    var media = option.image
      ? join([
          '<span class="mqz-product__media">',
          option.badge ? '<span class="mqz-product__badge">' + badge(option.badge) + '</span>' : '',
          '<img src="' + esc(option.image) + '" alt="' + esc(option.imageAlt || option.title) + '" loading="lazy" decoding="async">',
          '<span class="mqz-product__media-veil" aria-hidden="true"></span>',
          '</span>'
        ])
      : '';

    return join([
      '<button class="mqz-card mqz-product' +
        (option.featured ? ' mqz-product--featured' : '') +
        (option.image ? '' : ' mqz-product--no-media') +
        (ctx.selected ? ' is-selected' : '') +
        '"',
      cardAttrs({ id: option.id, role: 'radio', selected: ctx.selected }),
      '>',

      media,

      '<span class="mqz-product__body">',
      option.image || !option.badge ? '' : '<span class="mqz-product__badge-inline">' + badge(option.badge) + '</span>',

      /* Title and the radio dial share a row, so the dial can never sit on
         top of the copy the way a floating tick does. */
      '<span class="mqz-product__head">',
      '<span class="mqz-product__title">' + esc(option.title) + '</span>',
      '<span class="mqz-card__radio" aria-hidden="true">' + icon('check') + '</span>',
      '</span>',

      option.subtitle ? '<span class="mqz-product__subtitle">' + esc(option.subtitle) + '</span>' : '',
      option.description ? '<span class="mqz-product__desc">' + esc(option.description) + '</span>' : '',
      benefitList(option.benefits, 'mqz-product__benefits'),
      stars,
      option.footerImage
        ? '<span class="mqz-product__logos"><img src="' +
          esc(option.footerImage) +
          '" alt="' +
          esc(option.footerImageAlt || '') +
          '" loading="lazy" decoding="async"></span>'
        : '',

      hasPrice
        ? join([
            '<span class="mqz-product__foot">',
            priceBlock(option.price, option.compareAtPrice, option.priceNote, ctx.formatMoney),
            '</span>'
          ])
        : '',
      '</span>',
      '</button>'
    ]);
  }

  /** Image-led card. The photograph carries the story; the label is one line. */
  function GoalCard(option, ctx) {
    var accentVar = option.accent ? ' style="--mqz-goal-accent:' + esc(option.accent) + '"' : '';

    return join([
      '<button class="mqz-card mqz-goal' + (ctx.selected ? ' is-selected' : '') + '"',
      accentVar,
      cardAttrs({ id: option.id, role: 'checkbox', selected: ctx.selected }),
      '>',
      checkMark(),

      option.image
        ? '<span class="mqz-goal__media"><img src="' +
          esc(option.image) +
          '" alt="' +
          esc(option.imageAlt || option.title) +
          '" loading="lazy" decoding="async"></span>'
        : '',
      '<span class="mqz-goal__scrim" aria-hidden="true"></span>',

      '<span class="mqz-goal__body">',
      option.icon ? '<span class="mqz-goal__icon" aria-hidden="true">' + icon(option.icon) + '</span>' : '',
      '<span class="mqz-goal__title">' + esc(option.title) + '</span>',
      '</span>',
      '</button>'
    ]);
  }

  /* ======================================================================
     TextChoiceCard  — compact text-only options (goals step)
     ====================================================================== */

  /** Text-only selectable tile — no image, icon or accent. */
  function TextChoiceCard(option, ctx) {
    return join([
      '<button class="mqz-card mqz-text' + (ctx.selected ? ' is-selected' : '') + '"',
      cardAttrs({ id: option.id, role: ctx.inputRole || 'radio', selected: ctx.selected }),
      '>',
      '<span class="mqz-text__label">' + esc(option.title) + '</span>',
      '</button>'
    ]);
  }

  /* ======================================================================
     OptionCard  — Step 3 (yes / no)
     ====================================================================== */

  /** Horizontal icon-led choice card. */
  function OptionCard(option, ctx) {
    return join([
      '<button class="mqz-card mqz-option' + (ctx.selected ? ' is-selected' : '') + '"',
      cardAttrs({ id: option.id, role: 'radio', selected: ctx.selected }),
      '>',
      checkMark(),

      option.icon ? '<span class="mqz-option__icon" aria-hidden="true">' + icon(option.icon) + '</span>' : '',

      '<span class="mqz-option__text">',
      '<span class="mqz-option__title">' + esc(option.title) + badge(option.badge) + '</span>',
      option.subtitle ? '<span class="mqz-option__subtitle">' + esc(option.subtitle) + '</span>' : '',
      '</span>',
      '</button>'
    ]);
  }

  /* ======================================================================
     TestimonialCard
     ====================================================================== */

  function TestimonialCard(testimonial) {
    if (!testimonial || !testimonial.quote) return '';

    return join([
      '<figure class="mqz-testimonial">',
      testimonial.image
        ? '<img class="mqz-testimonial__avatar" src="' +
          esc(testimonial.image) +
          '" alt="' +
          esc(testimonial.imageAlt || testimonial.name || '') +
          '" loading="lazy" decoding="async">'
        : '',

      '<div>',
      '<blockquote class="mqz-testimonial__quote">&ldquo;' + esc(testimonial.quote) + '&rdquo;</blockquote>',
      '<figcaption class="mqz-testimonial__attrib">',
      testimonial.name ? '<span class="mqz-testimonial__name">' + esc(testimonial.name) + '</span>' : '',
      testimonial.title ? '<span class="mqz-testimonial__role">' + esc(testimonial.title) + '</span>' : '',
      testimonial.logo
        ? '<img class="mqz-testimonial__logo" src="' +
          esc(testimonial.logo) +
          '" alt="' +
          esc(testimonial.logoAlt || '') +
          '" loading="lazy" decoding="async">'
        : '',
      '</figcaption>',
      '</div>',
      '</figure>'
    ]);
  }

  /* ======================================================================
     PricingCard  — Step 4 (plans)
     ====================================================================== */

  /**
   * Plan row. Reads left to right like the other stacked steps: dial, plan
   * name and its commitment line, then the money on the right — price, the
   * billing note, and the DNA perk beneath it. The plan badge pins to the
   * card's top right corner. Plan benefits stay in the config; the summary and
   * the upgrade offer are what render them.
   */
  function PricingCard(plan, ctx) {
    var featured = plan.featured ? ' mqz-plan--featured' : '';

    /* A plan can bill on one cadence but read better quoted on another (annual
       is quoted per month), so `cardPrice` replaces the headline figure here
       while priceDueToday stays the amount the cart and summary work from. */
    var quoted = plan.cardPrice !== null && plan.cardPrice !== undefined;
    var rowPrice = quoted ? plan.cardPrice : plan.priceDueToday;
    var rowUnit = quoted ? plan.cardPriceUnit : plan.priceUnit;
    var rowWas = quoted ? plan.cardPriceCompareAt : plan.priceCompareAt;

    var noteTokens =
      plan.perMonthAmount === null || plan.perMonthAmount === undefined
        ? null
        : { amount: plan.perMonthAmount };

    return join([
      '<button class="mqz-card mqz-plan' + featured + (ctx.selected ? ' is-selected' : '') + '"',
      cardAttrs({ id: plan.id, role: 'radio', selected: ctx.selected }),
      '>',

      '<span class="mqz-card__radio" aria-hidden="true">' + icon('check') + '</span>',

      /* The badge is taken out of flow by the CSS so it pins to the card's top
         right corner, clear of the price column below it. */
      plan.badge
        ? '<span class="mqz-plan__badge">' + badge(plan.badge, plan.featured ? 'solid' : null) + '</span>'
        : '',

      '<span class="mqz-plan__id">',
      '<span class="mqz-plan__name">' + esc(plan.name) + '</span>',
      plan.commitmentNote ? '<span class="mqz-plan__note">' + esc(plan.commitmentNote) + '</span>' : '',
      '</span>',

      '<span class="mqz-plan__money">',
      '<span class="mqz-plan__price">',
      '<span class="mqz-price">' + esc(ctx.formatMoney(rowPrice)) + '</span>',
      rowWas ? '<span class="mqz-price mqz-price--was">' + esc(ctx.formatMoney(rowWas)) + '</span>' : '',
      rowUnit ? '<span class="mqz-plan__unit">' + esc(rowUnit) + '</span>' : '',
      '</span>',

      plan.perMonthLabel
        ? '<span class="mqz-plan__permonth">' +
          esc(ctx.interpolate(plan.perMonthLabel, noteTokens)) +
          '</span>'
        : '',

      /* Whichever DNA perk this plan gives, resolved from the DNA answer. */
      ctx.perk ? '<span class="mqz-plan__perk">' + badge(ctx.perk, 'success') + '</span>' : '',

      plan.savingsLabel
        ? '<span class="mqz-plan__savings">' + badge(ctx.interpolate(plan.savingsLabel), 'success') + '</span>'
        : '',
      '</span>',
      '</button>'
    ]);
  }

  /* ======================================================================
     OrderSummary  — Step 5
     ====================================================================== */

  /**
   * Renders from a computed summary object (built by myoform-quiz.js), so it
   * always reflects current state and never stores anything itself.
   *
   * Every line is the same shape — photograph, product name, price — with no
   * heading, no framing panel and only a hairline between them. The goal the
   * user picked is not a line item: it rides to the cart as a property.
   */
  function OrderSummary(step, summary, ctx) {
    var sections = step.sections || {};
    var rows = [];

    /**
     * One line. `qualifier` follows the name in parentheses (the chosen plan),
     * `note` sits under the name for anything the price cannot say on its own
     * (when a deferred charge is taken), `was` draws a struck-through price
     * before the amount, and `amount` is already-formatted text so a row can
     * read "Free" instead of a figure.
     */
    function row(item) {
      return join([
        '<div class="mqz-sum__row">',
        item.image
          ? '<img class="mqz-sum__thumb" src="' +
            esc(item.image) +
            '" alt="' +
            esc(item.title) +
            '" loading="lazy" decoding="async">'
          : '',
        '<div class="mqz-sum__body">',
        '<p class="mqz-sum__title">',
        esc(item.title),
        item.qualifier ? ' <span class="mqz-sum__qualifier">(' + esc(item.qualifier) + ')</span>' : '',
        '</p>',
        item.note ? '<p class="mqz-sum__note">' + esc(item.note) + '</p>' : '',
        '</div>',
        '<span class="mqz-sum__side">',
        item.was !== null && item.was !== undefined
          ? '<span class="mqz-sum__was">' + esc(ctx.formatMoney(item.was)) + '</span>'
          : '',
        '<span class="mqz-sum__amount' + (item.amountClass ? ' ' + item.amountClass : '') + '">' +
          esc(item.amount) +
          '</span>',
        '</span>',
        '</div>'
      ]);
    }

    /* DNA */
    if (summary.dna) {
      rows.push(
        row({
          image: summary.dna.image,
          title: summary.dna.title,
          was: summary.dna.wasAmount,
          amount: summary.dna.amount === 0 ? 'Free' : ctx.formatMoney(summary.dna.amount),
          amountClass: summary.dna.amount === 0 ? 'mqz-sum__amount--free' : ''
        })
      );
    }

    /* Supplements declined — the line stays so the decision is still visible.
       It has no product, so the section label names it and the answer sits
       where a price would. */
    if (summary.plan && summary.plan.declined) {
      rows.push(
        row({
          title: (sections.supplements && sections.supplements.label) || 'Your supplements',
          amount: ctx.labels.supplementsDeclined || 'Not included',
          amountClass: 'mqz-sum__amount--muted'
        })
      );
    }

    /* Supplements + plan */
    if (summary.plan && !summary.plan.declined) {
      rows.push(
        row({
          image: summary.plan.image,
          title: summary.plan.title,
          qualifier: summary.plan.planName,
          was: summary.plan.wasAmount,
          note: summary.plan.note,
          /* A deferred plan quotes its recurring price rather than a figure
             due today, and the note under the name says when it is taken. */
          amount: summary.plan.deferredAmount || ctx.formatMoney(summary.plan.amount),
          amountClass: summary.plan.deferredAmount ? 'mqz-sum__amount--later' : ''
        })
      );
    }

    /* Free gifts — one row each, in the order the plan earns them */
    (summary.gifts || []).forEach(function (gift) {
      rows.push(
        row({
          image: gift.image,
          title: gift.title,
          was: gift.wasAmount,
          amount: 'Free',
          amountClass: 'mqz-sum__amount--free'
        })
      );
    });

    /* Line items, any offer, then the trust row. There is no totals panel: the
       sticky bar already carries the running total, the billing note and
       checkout, so a second copy of them under the lines only repeated itself.
       That makes the trust row the last thing before the checkout button. */
    return join([
      '<div class="mqz__summary">',
      '<div class="mqz-sum">' + rows.join('') + '</div>',
      summary.extras || '',
      NextSteps(step.nextSteps),
      TrustRow(ctx.trust),
      '</div>'
    ]);
  }

  /** What follows checkout, set between the line items and the trust row. */
  function NextSteps(next) {
    if (!next || !next.copy) return '';

    return join([
      '<aside class="mqz-next">',
      next.headline ? '<h2 class="mqz-next__title">' + esc(next.headline) + '</h2>' : '',
      '<p class="mqz-next__copy">' + esc(next.copy) + '</p>',
      '</aside>'
    ]);
  }

  /**
   * Reassurance and accepted payments, at the foot of the summary column and
   * so directly above the checkout button.
   *
   * `paymentIcons` is inserted unescaped: it is Shopify's own payment badge
   * markup, built by payment_type_svg_tag in quiz-config.liquid, not anything
   * a shopper can reach.
   */
  function TrustRow(trust) {
    if (!trust) return '';

    var points = trust.points || [];
    if (!points.length && !trust.paymentIcons) return '';

    return join([
      '<div class="mqz-trust">',
      points.length
        ? '<p class="mqz-trust__points">' +
          points
            .map(function (point) {
              return '<span>' + esc(point) + '</span>';
            })
            .join('') +
          '</p>'
        : '',
      trust.paymentIcons
        ? join([
            '<p class="mqz-trust__payments">',
            trust.paymentsLabel
              ? '<span class="mqz-trust__label">' + esc(trust.paymentsLabel) + '</span>'
              : '',
            '<span class="mqz-trust__icons">' + trust.paymentIcons + '</span>',
            '</p>'
          ])
        : '',
      '</div>'
    ]);
  }

  /* ======================================================================
     UpsellCard
     ====================================================================== */

  /**
   * Two shapes, one component:
   *   variant "add"     — offer a plan to someone who declined supplements
   *   variant "upgrade" — swap the currently selected plan for a better one
   * Both act in place; neither sends the user back through the quiz.
   */
  function UpsellCard(config, ctx) {
    var isUpgrade = config.variant === 'upgrade';
    var action = isUpgrade ? 'data-mqz-upgrade' : 'data-mqz-upsell';
    var dismiss = isUpgrade ? 'data-mqz-upgrade-dismiss' : 'data-mqz-upsell-dismiss';
    var offerTitle = config.offerTitle || config.optionLabel || '';
    var offerNote = config.offerNote || '';
    var priceSuffix = config.priceSuffix || '';
    var hasPrice = config.price !== null && config.price !== undefined;

    var media = config.image
      ? '<div class="mqz-upsell__media"><img src="' +
        esc(config.image) +
        '" alt="' +
        esc(config.imageAlt || '') +
        '" loading="lazy" decoding="async"></div>'
      : '';

    var hasOffer = media || offerTitle || hasPrice;

    return join([
      '<div class="mqz-upsell">',
      config.eyebrow ? '<p class="mqz__eyebrow">' + esc(config.eyebrow) + '</p>' : '',
      '<h2 class="mqz-upsell__headline">' + esc(config.headline) + '</h2>',
      config.copy ? '<p class="mqz-upsell__copy">' + esc(config.copy) + '</p>' : '',
      benefitList(
        (config.benefits || []).map(function (b) {
          return ctx.interpolate(b);
        }),
        'mqz-upsell__benefits'
      ),

      hasOffer
        ? join([
            '<div class="mqz-upsell__offer">',
            media,
            offerTitle || offerNote
              ? join([
                  '<div class="mqz-upsell__offer-copy">',
                  offerTitle ? '<p class="mqz-upsell__offer-title">' + esc(offerTitle) + '</p>' : '',
                  offerNote
                    ? '<p class="mqz-upsell__note"><span class="mqz-upsell__note-icon" aria-hidden="true">' +
                      icon('refresh') +
                      '</span>' +
                      esc(offerNote) +
                      '</p>'
                    : '',
                  '</div>'
                ])
              : '',
            hasPrice ? '<span class="mqz-upsell__rule" aria-hidden="true"></span>' : '',
            hasPrice
              ? join([
                  '<p class="mqz-upsell__pricing">',
                  config.compareAtPrice
                    ? '<span class="mqz-price mqz-price--was">' +
                      esc(ctx.formatMoney(config.compareAtPrice)) +
                      '</span>'
                    : '',
                  '<span class="mqz-price">' + esc(ctx.formatMoney(config.price)) + '</span>',
                  priceSuffix
                    ? '<span class="mqz-upsell__cadence">' + esc(priceSuffix) + '</span>'
                    : '',
                  '</p>'
                ])
              : '',
            '</div>'
          ])
        : '',

      '<div class="mqz-upsell__actions">',
      '<button type="button" class="mqz-btn mqz-upsell__cta" ' +
        action +
        '>' +
        esc(config.cta) +
        '<span class="mqz-upsell__cta-arrow" aria-hidden="true">' +
        icon('arrowRight') +
        '</span></button>',
      config.secondaryCta
        ? '<button type="button" class="mqz-btn mqz-btn--link mqz-upsell__dismiss" ' +
          dismiss +
          '>' +
          esc(config.secondaryCta) +
          '</button>'
        : '',
      '</div>',
      '</div>'
    ]);
  }

  /* ======================================================================
     StickyContinueButton  +  inline footer actions
     ====================================================================== */

  /**
   * Footer for a selection step: inline CTA on desktop, reassurance row and
   * an error slot. The mobile CTA lives in StickyContinueButton.
   */
  function StepFooter(step, ctx) {
    return join([
      '<div class="mqz__actions">',
      '<p class="mqz__error" data-mqz-error hidden role="alert"></p>',
      '<button type="button" class="mqz-btn mqz-btn--primary" data-mqz-next>' +
        esc(step.cta || ctx.labels.continue || 'Continue') +
        '</button>',
      '</div>'
    ]);
  }

  /**
   * Persistent mobile action bar. Shows the running total once there is
   * something to show, otherwise a prompt.
   */
  function StickyContinueButton(state) {
    var ctaOnly = Boolean(state.ctaOnly);
    var summaryBlock = ctaOnly
      ? ''
      : join([
          '<div class="mqz__sticky-summary">',
          '<span class="mqz__sticky-money">',
          state.totalLabel
            ? '<span class="mqz__sticky-label">' + esc(state.totalLabel) + '</span>'
            : '',
          state.total
            ? '<span class="mqz__sticky-total" data-mqz-sticky-total>' + esc(state.total) + '</span>'
            : '',
          '</span>',
          /* Names anything charged after checkout, so the total above can be
             read as today's charge alone. */
          state.note ? '<span class="mqz__sticky-note">' + esc(state.note) + '</span>' : '',
          '</div>'
        ]);

    return join([
      '<div class="mqz__sticky' +
        (state.visible ? ' is-visible' : '') +
        (ctaOnly ? ' is-cta-only' : '') +
        '" data-mqz-sticky>',
      '<div class="mqz__sticky-inner">',
      summaryBlock,
      '<button type="button" class="mqz-btn' +
        (ctaOnly ? ' mqz-btn--full-width' : '') +
        '" data-mqz-sticky-cta' +
        (state.enabled ? '' : ' disabled') +
        '>',
      '<span data-mqz-sticky-label>' + esc(state.label || 'Continue') + '</span>',
      '</button>',
      '</div>',
      '</div>'
    ]);
  }

  /* ======================================================================
     Export
     ====================================================================== */

  window.MyoQuizComponents = {
    QuizLayout: QuizLayout,
    QuizProgress: QuizProgress,
    OptionsNote: OptionsNote,
    Interstitial: Interstitial,
    OptionCard: OptionCard,
    ProductCard: ProductCard,
    GoalCard: GoalCard,
    TextChoiceCard: TextChoiceCard,
    TestimonialCard: TestimonialCard,
    PricingCard: PricingCard,
    OrderSummary: OrderSummary,
    UpsellCard: UpsellCard,
    StickyContinueButton: StickyContinueButton,
    StepFooter: StepFooter,
    icon: icon,
    badge: badge,
    escape: esc
  };
})();
